import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import TextFieldControl from "components/form/TextFieldControl";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { palette } from "theme";
import { PROMPT_SLIDES } from "../constants";
import { CompetencyMapGenerationFormValues, PromptSlideId } from "../types";

type PromptSlidesProps = {
  activeSlideId: PromptSlideId;
  skippedPromptIds: PromptSlideId[];
  onChangeActiveSlide: (slideId: PromptSlideId) => void;
  onToggleSkip: (slideId: PromptSlideId) => void;
};

const PROMPT_FIELD_NAMES_BY_SLIDE: Record<PromptSlideId, Array<keyof CompetencyMapGenerationFormValues>> = {
  globalPrompt: ["globalPrompt"],
  questionsPrompt: ["questionsPrompt"],
  criteriaPrompt: ["criteriaPrompt"],
  tonePrompt: ["tonePrompt", "toneContext"],
};

const requiredPromptRule = { required: "Обязательное поле" };

const PromptSlides = ({ activeSlideId, skippedPromptIds, onChangeActiveSlide, onToggleSkip }: PromptSlidesProps) => {
  const {
    clearErrors,
    formState: { errors },
  } = useFormContext<CompetencyMapGenerationFormValues>();

  const activeSlide = PROMPT_SLIDES.find((slide) => slide.id === activeSlideId) || PROMPT_SLIDES[0];
  const isActiveSlideSkipped = skippedPromptIds.includes(activeSlide.id);
  const hasPromptErrors = PROMPT_SLIDES.some(
    (slide) =>
      !skippedPromptIds.includes(slide.id) &&
      PROMPT_FIELD_NAMES_BY_SLIDE[slide.id].some((fieldName) => Boolean(errors[fieldName]))
  );

  useEffect(() => {
    skippedPromptIds.forEach((slideId) => {
      PROMPT_FIELD_NAMES_BY_SLIDE[slideId].forEach((fieldName) => clearErrors(fieldName));
    });
  }, [clearErrors, skippedPromptIds]);

  const skipRecoverButton = (
    <Button
      variant='text'
      onClick={() => onToggleSkip(activeSlide.id)}
      sx={{ alignSelf: { xs: "flex-start", sm: "center" }, wordBreak: "normal" }}>
      {isActiveSlideSkipped ? "Восстановить" : "Пропустить"}
    </Button>
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        spacing={1}
        useFlexGap
        flexWrap='wrap'>
        <Stack
          direction='row'
          spacing={1}
          useFlexGap
          alignItems='center'
          flexWrap='wrap'>
          {PROMPT_SLIDES.map((slide) => {
            const isSkipped = skippedPromptIds.includes(slide.id);
            const isActive = slide.id === activeSlideId;

            return (
              <Button
                key={slide.id}
                variant={isActive ? "circleContained" : "circleOutlined"}
                color={isSkipped ? "inherit" : "primary"}
                onClick={() => onChangeActiveSlide(slide.id)}
                sx={{
                  textDecoration: isSkipped ? "line-through" : "none",
                  opacity: isSkipped && !isActive ? 0.65 : 1,
                  border: `1.5px solid ${palette.border}`,
                  fontSize: 12,
                  textTransform: "uppercase",
                }}>
                {slide.icon}
              </Button>
            );
          })}
        </Stack>
        <Stack
          direction='row'
          justifyContent='space-between'
          spacing={1}
          alignItems='center'
          flexWrap='wrap'>
          <Typography
            variant='subtitle2'
            color='text.secondary'>
            обязательное · можно&#160;пропустить
          </Typography>
          {skipRecoverButton}
        </Stack>
      </Stack>
      {hasPromptErrors && <Typography color='error'>Заполнены не все промпты</Typography>}
      <Divider />
      <Box>
        <Stack
          spacing={2}
          height='100%'>
          {isActiveSlideSkipped ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems='center'
              justifyContent='center'>
              <Typography color='text.secondary'>Попущен</Typography>
              {skipRecoverButton}
            </Stack>
          ) : (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems='center'>
              <Typography
                variant='body16Extra'
                color='text.secondary'>
                {activeSlide.title}
              </Typography>
              <Typography
                variant='subtitle2'
                color='text.secondary'>
                {" "}
                — {activeSlide.description}
              </Typography>
            </Stack>
          )}
          <Box sx={{ display: activeSlide.id === "globalPrompt" && !isActiveSlideSkipped ? "block" : "none" }}>
            <TextFieldControl
              name='globalPrompt'
              rules={requiredPromptRule}
              disabled={skippedPromptIds.includes("globalPrompt")}
              multiline
              minRows={4}
              maxRows={6}
              placeholder='Например: упор на рабочие ситуации и решение конкретных задач'
            />
          </Box>
          <Box sx={{ display: activeSlide.id === "questionsPrompt" && !isActiveSlideSkipped ? "block" : "none" }}>
            <TextFieldControl
              name='questionsPrompt'
              rules={requiredPromptRule}
              disabled={skippedPromptIds.includes("questionsPrompt")}
              multiline
              minRows={4}
              maxRows={6}
              placeholder='Например: в кейсах просить указывать альтернативы и обосновывать выбор'
            />
          </Box>
          <Box sx={{ display: activeSlide.id === "criteriaPrompt" && !isActiveSlideSkipped ? "block" : "none" }}>
            <TextFieldControl
              name='criteriaPrompt'
              rules={requiredPromptRule}
              disabled={skippedPromptIds.includes("criteriaPrompt")}
              multiline
              minRows={4}
              maxRows={6}
              placeholder='Например: критичная ошибка — нарушение регламента или риск для безопасности'
            />
          </Box>
          <Box sx={{ display: activeSlide.id === "tonePrompt" && !isActiveSlideSkipped ? "block" : "none" }}>
            <Stack spacing={2}>
              <TextFieldControl
                name='tonePrompt'
                rules={requiredPromptRule}
                disabled={skippedPromptIds.includes("tonePrompt")}
                label='Тональность'
                placeholder='Например: обращение на "вы", формально, без сленга'
              />
              <TextFieldControl
                name='toneContext'
                disabled={skippedPromptIds.includes("tonePrompt")}
                label='Дополнительный контекст'
                placeholder='Например: b2b-компания, enterprise, высокая роль регламентов'
              />
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

export default PromptSlides;
